/** Facts for the model WHS Act ss 35–37 categories. Victoria uses its OHS Act. */
export interface WhsNotifiabilityFacts {
  death: boolean | null;
  inpatientTreatment: boolean | null;
  listedSeriousInjury: boolean | null;
  dangerousIncident: boolean | null;
  /** Omitted on older callers: preserve their four-fact result. */
  medicalTreatmentAfterSubstanceExposure?: boolean | null;
}

/** Unknown remains review required. Ordinary medical treatment is not an inpatient admission. */
export function suggestWhsNotifiability(facts: WhsNotifiabilityFacts): boolean | null {
  const values = Object.values(facts);
  if (values.some((value) => value === true)) return true;
  if (values.some((value) => value === null)) return null;
  return false;
}

export const WHS_REGULATORS = {
  ACT: 'WorkSafe ACT',
  NSW: 'SafeWork NSW',
  NT: 'NT WorkSafe',
  QLD: 'Workplace Health and Safety Queensland',
  SA: 'SafeWork SA',
  TAS: 'WorkSafe Tasmania',
  VIC: 'WorkSafe Victoria',
  WA: 'WorkSafe Western Australia',
} as const;

export type WhsJurisdiction = keyof typeof WHS_REGULATORS;

/** The UI presents a prompt; the provider records the final decision. */
export function whsNotifiabilityPrompt(value: boolean | null): string {
  const recommendation =
    value === true
      ? 'Potentially notifiable: ask the WHS officer to review the facts.'
      : value === false
        ? 'None of the recorded facts indicates notifiability. The WHS officer must review.'
        : 'Notifiability needs review: one or more recorded facts are unknown.';
  return `${recommendation} This recommendation is an aid to review based on the recorded facts. It does not cover every circumstance in which your jurisdiction requires notification. The provider decides; check the applicable requirements.`;
}

/** U-16: state belongs to the selected service location, never the tenant. */
export function whsSiteWordingKey(siteId: string | null, siteState?: string | null) {
  return siteId && siteState ? `site:${siteId}:whs:${siteState.toUpperCase()}` : 'whs:neutral';
}
export const WHS_EXPOSURE_QUESTION =
  'Did anyone need medical treatment within 48 hours after exposure to a substance (for example blood or body fluids)?';
export const WHS_CLINICAL_FOLLOW_UP_HELP =
  'Source-person follow-up is managed by the clinical lead with consent, outside the incident record. Keep treatment details, test results and blood-borne-virus status out of every incident field.';
export function whsSiteWording(key: string) {
  const state = /^site:.+:whs:(NSW|VIC|TAS)$/.exec(key)?.[1];
  return {
    review:
      'Tell your manager now if someone may have been seriously hurt or exposed to a dangerous event. The office will review notification requirements for this service location.',
    exposure: WHS_EXPOSURE_QUESTION,
    exposureHelp:
      state === 'NSW'
        ? 'SafeWork NSW guidance: medical treatment means treatment by a registered medical practitioner (a doctor). Only the most serious blood, body substance and needle-stick exposure incidents need to be notified. Ask the WHS officer to review the facts.'
        : state === 'VIC'
          ? 'Treatment by a registered doctor, nurse or midwife counts as medical treatment. WorkSafe Victoria lists human blood and body substances as exposure to a substance. Check whether the incident happened at a workplace under your management and control.'
          : state === 'TAS'
            ? 'The Tasmanian WHS Act includes medical treatment within 48 hours of exposure to a substance. WorkSafe Tasmania guidance was not available to check.'
            : 'Record what is known; choose not sure if treatment needs are unknown. The office checks the requirements for the service location.',
    clinicalFollowUp: WHS_CLINICAL_FOLLOW_UP_HELP,
  };
}
export function hasSubstanceExposure(chips: readonly string[]) {
  return chips.some((chip) => ['needlestick', 'substance_exposure'].includes(chip));
}
